import React, { useEffect, useState } from 'react';
import { Modal } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import axiosInstance from '../config';

const useStyles = makeStyles((theme) => ({
  modalStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  box: {
    backgroundColor: '#fff',
    width: 'min(760px, 96vw)',
    maxHeight: '94vh',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.4rem 0.6rem',
    borderBottom: '1px solid #dedede',
  },
  title: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#0E2336',
    margin: '0 !important',
  },
  close: {
    backgroundColor: '#fff',
    color: '#86898C',
    fontWeight: 'bold',
    fontSize: '12px',
    lineHeight: '12px',
    border: '1px solid #86898C',
    borderRadius: '4px',
    cursor: 'pointer',
    padding: '0.2rem 0.5rem',
  },
  body: {
    flex: 1,
    minHeight: '60vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  frame: {
    width: '100%',
    height: '84vh',
    border: 'none',
  },
  message: {
    fontSize: '13px',
    color: '#323030',
    textAlign: 'center',
    padding: '1rem 0.75rem',
    margin: 0,
  },
}));

/**
 * The vendor's own trade-detail page for one games bet, shown inside the portal.
 *
 * The page is the vendor's, not ours: it is embedded rather than rebuilt because a bet's legs, odds and combo
 * structure only exist on their side (our callback bodies carry identifiers and money, nothing else), and a
 * rebuild would have to keep pace with their settlement rules forever. Embedding also keeps one shape for both
 * populations - a WeGa sports ticket renders its own detail inline, a games ticket brings its provider report
 * in behind it.
 *
 * The URL is a ticket: it is handed straight to the frame, and the frame is opened only while the modal is up,
 * so nothing is left behind for the next row or the next operator. The vendor's page is the only way through by
 * choice - it is not offered again in a tab of its own or a second window.
 *
 * @param endpoint - the acting screen family's trade-detail endpoint (the reports' or the balance report's)
 * @param pubId    - the public id of the row the operator opened, or null when nothing is being shown
 * @param onClose  - closes the modal (the frame and the ticket go with it)
 */
const TradeDetailModal = ({ endpoint, pubId, onClose }) => {
  const classes = useStyles();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pubId || !endpoint) {
      setDetail(null);
      setLoading(false);
      return undefined;
    }

    // Closing while the ticket is being minted must not paint it later: a ticket reads one bet, and it must not
    // land under whatever row the operator opened next.
    let dropped = false;
    setDetail(null);
    setLoading(true);
    axiosInstance
      .get(endpoint, { params: { pubId } })
      .then((res) => {
        if (!dropped) setDetail(res.data);
      })
      .catch(() => {
        if (!dropped) setDetail({ status: false });
      })
      .finally(() => {
        if (!dropped) setLoading(false);
      });

    return () => {
      dropped = true;
    };
  }, [endpoint, pubId]);

  const url = detail && detail.status && detail.url ? detail.url : null;

  return (
    <Modal
      open={!!pubId}
      onClose={onClose}
      aria-labelledby="trade-detail-title"
      className={classes.modalStyle}
    >
      <div className={classes.box}>
        <div className={classes.head}>
          <p className={classes.title} id="trade-detail-title">
            Trade Detail
          </p>
          {/* the ticket is shown here and nowhere else: the vendor's page is not offered in a tab of its own */}
          <button className={classes.close} onClick={onClose}>
            Close
          </button>
        </div>
        <div className={classes.body}>
          {loading ? (
            <p className={classes.message}>Loading detail...</p>
          ) : url ? (
            <iframe title="trade-detail" src={url} referrerPolicy="no-referrer" className={classes.frame} />
          ) : (
            <p className={classes.message}>Unable to load detail. Please try again.</p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TradeDetailModal;
